/**
 * Protected Route Component for AICC Admin
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 * Optionally checks for specific roles.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import { hasAnyRole, UserRole } from '@/types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

/**
 * Protected Route Component
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // With role requirement
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
 *       <AdminPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export default function ProtectedRoute({
  children,
  requiredRoles,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user } = useAdminAuthStore();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = hasAnyRole(user, requiredRoles);

    if (!hasRequiredRole) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background">
          <ShieldAlert className="h-20 w-20 text-destructive mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            접근 권한이 없습니다
          </h2>
          <p className="text-muted-foreground mb-4">
            이 페이지에 접근할 권한이 없습니다.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            이전 페이지로 돌아가기
          </button>
        </div>
      );
    }
  }

  // Render children if authenticated and authorized
  return <>{children}</>;
}
